import {
    DefaultDevBoxTunnelHostingResourceName,
    DevBoxAddOnType,
    DevBoxHyperVProvisioningState,
    DevBoxHyperVStatus,
} from '../constants/add-ons';
import { DevBoxProvisioningState } from '../constants/dev-box';
import { DevBoxAddOn, DevBoxHyperVAddOn, DevBoxTunnelAddOn } from '../models/add-ons';
import { DevBox } from '../models/dev-box';
import { DevBoxState } from '../redux/store/dev-box-state';
import { isUndefinedOrWhiteSpace } from './string';

/**
 * VS Code extension constants for DevBox integration
 */
export const VSCodeDevBoxExtensionId = 'DevCenter.ms-devbox';
export const VSCodeScheme = 'vscode';
export const VSCodeConnectCommand = 'connect';
export const VSCodeDevBoxIdParameter = 'devBoxId';

/**
 * Still returning DevBoxState since if an add-on is present - its state should take precedence over host devbox state
 */
export const getDevBoxHyperVAddOnState = (addOn: DevBoxHyperVAddOn, devBox: DevBox): DevBoxState => {
    const { provisioningState } = devBox;

    // For now, deleting hyper-v is equivalent to deleting the devbox, so considering dev box provisioning state, not add-on state.
    if (provisioningState === DevBoxProvisioningState.Deleting) {
        return DevBoxState.Deleting;
    }

    const devBoxHyperVProvisioningState = addOn.provisioningState;
    const devBoxAddOnHyperVStatus = addOn.devBoxHyperVStatus;

    // Check provisioning state first, as it takes precedence over the action state.
    if (!!devBoxHyperVProvisioningState) {
        switch (devBoxHyperVProvisioningState) {
            case DevBoxHyperVProvisioningState.Failed:
                return DevBoxState.Failed;
            case DevBoxHyperVProvisioningState.Creating:
                return DevBoxState.Creating;
        }
    }

    // Check action state next.
    if (!!devBoxAddOnHyperVStatus) {
        switch (devBoxAddOnHyperVStatus) {
            case DevBoxHyperVStatus.Stopped:
                return DevBoxState.Stopped;
            case DevBoxHyperVStatus.Starting:
                return DevBoxState.Starting;
            case DevBoxHyperVStatus.Running:
                return DevBoxState.Running;
            case DevBoxHyperVStatus.Stopping:
                return DevBoxState.Stopping;
        }
    }

    // If we reach here, assume devbox is in a failed state.
    return DevBoxState.Failed;
};

/**
 * Filters tunnel addons from a list of addons and finds the primary tunnel
 * (the one with undefined or whitespace hostingResourceName).
 *
 * This follows the pattern used in dev-box-card.tsx and incorporates the
 * hostResourceName.isUndefinedOrWhiteSpace logic as specified in the README guidance.
 *
 * @param addOns Array of DevBox addons to filter
 * @returns The primary tunnel addon if found, undefined otherwise
 */
export const findPrimaryTunnelAddon = (addOns: DevBoxAddOn[]): DevBoxTunnelAddOn | undefined => {
    if (!addOns || addOns.length === 0) {
        return undefined;
    }

    // First filter to get only tunnel addons
    const tunnelAddOns = addOns.filter(
        (addOn: DevBoxAddOn): addOn is DevBoxTunnelAddOn => addOn.type === DevBoxAddOnType.DevBoxTunnel
    );

    if (tunnelAddOns.length === 0) {
        return undefined;
    }

    // Find the tunnel with undefined or whitespace hostingResourceName - indicating it's the primary tunnel (windows tunnel)
    // Also consider tunnels with 'Default' hostingResourceName as primary tunnels
    // This is the primary tunnel that should be used for connections
    return tunnelAddOns.find(
        (tunnel) =>
            isUndefinedOrWhiteSpace(tunnel.hostingResourceName) ||
            tunnel.hostingResourceName?.toLowerCase() === DefaultDevBoxTunnelHostingResourceName.toLowerCase()
    );
};

/**
 * Determines if a tunnel can be connected based on the tunnel addon state
 * @param tunnelAddOn The DevBox tunnel addon to check
 * @returns True if the tunnel is enabled and has a valid URI
 */
export const getTunnelCanBeConnected = (tunnelAddOn: DevBoxTunnelAddOn): boolean => {
    if (!tunnelAddOn) {
        return false;
    }

    const tunnelEnabled = tunnelAddOn.enabled;
    const codeTunnelUrl = tunnelAddOn.codeTunnelUrl;

    // Handle null values explicitly before calling isUndefinedOrWhiteSpace
    const uriIsValid = codeTunnelUrl !== null && !isUndefinedOrWhiteSpace(codeTunnelUrl);

    return tunnelEnabled && uriIsValid;
};

/**
 * Checks if a string matches the pattern [YYYY-MM-DD HH:mm:ss] UserSessionNotAvailable
 * @param code The string to check
 * @returns True if the string matches the expected pattern, false otherwise
 */
export const isUserSessionNotAvailableForTunnelCode = (code: string | undefined): boolean => {
    if (!code || typeof code !== 'string') {
        return false;
    }

    // Pattern: [YYYY-MM-DD HH:mm:ss] UserSessionNotAvailable
    // Using regex to match the exact format
    const pattern = /^\[\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\] UserSessionNotAvailable$/;

    return pattern.test(code);
};

export const createVSCodeDevBoxUri = (devBoxName: string): string => {
    return `${VSCodeScheme}://${VSCodeDevBoxExtensionId}/${VSCodeConnectCommand}?${VSCodeDevBoxIdParameter}=${devBoxName}`;
};
